import React, { useState } from "react";
import {
  Box,
  Collapse,
  Stack,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
} from "@mui/material";
import { ExpandMore as ExpandIcon } from "@mui/icons-material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { JobResponse, ErrorResponse } from "@taxassist/api-client";
import {
  ProcessingArtifacts,
  type ProcessingArtifactsRef,
} from "./ProcessingArtifacts";
import type { FileItem } from "./fileList";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { downloadAllProcessingResults } from "../../utils/filePreviewUtils";
import { useNotification } from "../../hooks/useNotification";
import { useAuth } from "../../contexts/AuthContext";
import { getStatusDisplay } from "../../utils/statusMapping";

// Define the artifact type to match the API client expectations
type ArtifactType = "binder" | "k1_worksheets" | "supporting_doc" | "other";

interface JobResultsSectionProps {
  job: JobResponse;
  processingArtifactItems: FileItem[];
  isLoadingProcessingResults: boolean;
  processingResultsError: ErrorResponse | null;
  context: "client" | "ops";
  processingArtifactsRef: React.RefObject<ProcessingArtifactsRef>;
  onUploadClick: () => void;
  onArtifactDownload: (file: FileItem) => Promise<void>;
  onArtifactPreview: (file: FileItem, index: number, files: FileItem[]) => void;
  onArtifactDelete?: (file: FileItem) => Promise<void>;
  onProcessingResultUpload?: (
    file: File,
    artifactType: ArtifactType,
    description?: string,
  ) => Promise<void>;
  getProcessingResultFileUrl: (file: FileItem) => Promise<string>;
  onArtifactUpdate?: () => void;
  uploadedFileItems?: FileItem[];
}

export function JobResultsSection({
  job,
  processingArtifactItems,
  isLoadingProcessingResults,
  processingResultsError,
  context,
  processingArtifactsRef,
  onUploadClick,
  onArtifactDownload,
  onArtifactPreview,
  onArtifactDelete,
  onProcessingResultUpload,
  getProcessingResultFileUrl,
  onArtifactUpdate,
  uploadedFileItems = [],
}: JobResultsSectionProps) {
  const getInitialOpenState = () => {
    if (context !== "client") return true;

    const simplifiedStatus = getStatusDisplay(job.status);

    if (
      simplifiedStatus === "Unsubmitted" ||
      simplifiedStatus === "New Files"
    ) {
      return false;
    }

    if (simplifiedStatus === "Queued" || simplifiedStatus === "In Progress") {
      return false;
    }

    if (simplifiedStatus === "Done") {
      return true;
    }

    return true;
  };

  const [open, setOpen] = useState(getInitialOpenState());
  const [downloadingAll, setDownloadingAll] = useState(false);
  const notification = useNotification();
  const { user } = useAuth();

  // For reviewers, this is step 2 (since they don't see the Process timeline)
  // For others in ops context, this is step 3
  const isReviewer = user?.role === "reviewer";
  const stepNumber = context === "ops" && isReviewer ? "2" : "3";

  const handleToggle = () => {
    setOpen(!open);
  };

  // Count complex K1s and unsupported files for warning message
  const { complexK1sCount, unsupportedCount } = uploadedFileItems.reduce(
    (counts, file) => {
      if (file.expectedBucket === "k1_complex") {
        counts.complexK1sCount++;
      } else if (file.expectedBucket === "unsupported") {
        counts.unsupportedCount++;
      }
      return counts;
    },
    { complexK1sCount: 0, unsupportedCount: 0 },
  );
  const totalNonStandardCount = complexK1sCount + unsupportedCount;

  // Show warning when job is in verification/done state
  // This means processing is complete and reviewer has moved it to ready for review
  const isInReviewState =
    job.status === "VERIFIED" ||
    job.status === "DONE";
  const showWarning = totalNonStandardCount > 0 && isInReviewState;

  // Handle download all processing results
  const handleDownloadAll = async () => {
    const jobId = job.jobId;
    if (!jobId) {
      console.error("Cannot download all: jobId is missing");
      notification.showError("Cannot download files: job ID is missing");
      return;
    }

    setDownloadingAll(true);
    try {
      // Call the backend to create a zip file and get download URL
      const { downloadUrl, fileName } =
        await downloadAllProcessingResults(jobId);

      // Trigger download by creating an anchor and clicking it
      const anchor = document.createElement("a");
      anchor.style.display = "none";
      anchor.href = downloadUrl;
      anchor.download = fileName;

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      notification.showSuccess("Downloading all files...");
    } catch (error) {
      console.error("Failed to download all files:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to download all files";
      notification.showError(errorMessage);
    } finally {
      setDownloadingAll(false);
    }
  };

  // Show download all button when job is done and has files (both client and ops contexts)
  const showDownloadAll =
    job.status === "DONE" &&
    processingArtifactItems.length > 0;

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#FFFFFF",
        borderRadius: 2,
        border: 1,
        borderColor: "#E7E7E7",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack direction="row">
          <IconButton
            aria-label="collapse"
            onClick={handleToggle}
            size="small"
            sx={{ transform: open ? "rotate(0)" : "rotate(-90deg)" }}
          >
            <ExpandIcon />
          </IconButton>
          <Stack gap={1} direction="row">
            <Typography variant="h6" sx={{ color: "text.primary" }}>
              {context === "ops" && <>{stepNumber}. Upload Deliverables</>}
              {context === "client" && <>3. Download Deliverables</>}
            </Typography>
            <Box>
              <Chip
                size="small"
                sx={{ color: "#757575", fontWeight: "bold" }}
                label={processingArtifactItems.length}
              />
            </Box>
          </Stack>
        </Stack>
        {context === "ops" && (
          <Button
            variant="text"
            startIcon={<FileUploadOutlinedIcon />}
            onClick={onUploadClick}
            size="small"
            sx={{ px: 2, fontWeight: 700 }}
          >
            Upload
          </Button>
        )}
        {showDownloadAll && (
          <Button
            variant="text"
            startIcon={
              downloadingAll ? (
                <CircularProgress size={16} />
              ) : (
                <FileDownloadOutlinedIcon />
              )
            }
            onClick={handleDownloadAll}
            size="small"
            disabled={!open || downloadingAll}
            sx={{ px: 2, fontWeight: 700 }}
          >
            Download All
          </Button>
        )}
      </Box>

      {/* Warning message for non-standard files */}
      {showWarning && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ mt: 2, mb: 1 }}
        >
          <Typography variant="body2">
            {complexK1sCount > 0 && unsupportedCount > 0 && (
              <>
                {complexK1sCount} Complex K1{complexK1sCount > 1 ? "s" : ""} and{" "}
                {unsupportedCount} unsupported file{unsupportedCount > 1 ? "s" : ""} could
                not go through standard submission.
              </>
            )}
            {complexK1sCount > 0 && unsupportedCount === 0 && (
              <>
                {complexK1sCount} Complex K1{complexK1sCount > 1 ? "s" : ""} could not go
                through standard submission.
              </>
            )}
            {complexK1sCount === 0 && unsupportedCount > 0 && (
              <>
                {unsupportedCount} unsupported file{unsupportedCount > 1 ? "s" : ""} could
                not go through standard submission.
              </>
            )}
          </Typography>
        </Alert>
      )}

      <Collapse in={open}>
        <ProcessingArtifacts
          ref={processingArtifactsRef}
          files={processingArtifactItems}
          isLoading={isLoadingProcessingResults}
          error={
            processingResultsError
              ? new Error(
                  processingResultsError.message ||
                    "Failed to load processing results",
                )
              : null
          }
          status={
            job.status.toLowerCase() as
              | "draft"
              | "submitted"
              | "triage"
              | "processing"
              | "done"
              | "error"
          }
          jobId={job.jobId}
          surePrepJobId={job.surePrepJobId}
          onDownload={onArtifactDownload}
          onPreview={(file, index) =>
            onArtifactPreview(
              file,
              processingArtifactItems.findIndex((f) => f.id === file.id),
              processingArtifactItems,
            )
          }
          onDelete={context === "ops" ? onArtifactDelete : undefined}
          onUpload={context === "ops" ? onProcessingResultUpload : undefined}
          getFileUrl={getProcessingResultFileUrl}
          isOpsContext={context === "ops"}
          embedded={true}
          onArtifactUpdate={onArtifactUpdate}
          onDownloadAll={handleDownloadAll}
          downloadingAll={downloadingAll}
        />
      </Collapse>

      {/* Notification Component */}
      {notification.NotificationComponent}
    </Box>
  );
}
